/**
 * @file: hooks.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ReportShareViewSummary } from '@euler/model/ReportShareViewSummary';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useMemo, useState } from 'react';
import { kStepCircleSize } from './constants';

export const useReportShareViewSummary = (orderNo: string | undefined) => {
  const { orderService } = useServiceFactory();
  const [summary, setSummary] = useState<ReportShareViewSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(orderNo));

  useEffect(() => {
    if (!orderNo) {
      setSummary(null);
      setLoading(false);
      return;
    }
    let canceled = false;
    setLoading(true);
    void (async () => {
      try {
        const result = await orderService.getReportShareViewSummary(orderNo, {
          withViewSummary: true,
          withShareSummary: true,
        });
        if (!canceled) setSummary(result);
      } catch (e) {
        if (!canceled) {
          console.warn('failed to load report share view summary:', e);
          setSummary(null);
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [orderNo, orderService]);

  return { summary, loading };
};

export const useTaskCardV2Metrics = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(() => {
    // Header: View has min-height of (image 58 + padding 12+12) = 82,
    // and that exceeds the actual content height (~76), so the min wins.
    const headerH = rpx(58 + 24);
    // DashedSeparator after the header.
    const sep1 = rpx(1);
    // Info grid: 10 pad-top + 2 rows (each = 3 pad + 16 line-height + 3 pad)
    // + 10 pad-bottom = 64
    const gridH = rpx(10) + (rpx(3) + rpx(16) + rpx(3)) * 2 + rpx(10);
    // DashedSeparator after the grid.
    const sep2 = rpx(1);
    // Status row: 10 pad-top + StatusBadgeV2 fixed height 20 + 12 pad-bottom.
    const statusH = rpx(10) + rpx(20) + rpx(12);
    // Progress section: 1 top border + 10 pad-top + header line 14
    // + 8 gap + circle 18 + 4 gap + label line 14 + 2 gap + view status 12
    // + 10 pad-bottom = 93
    const progressH =
      rpx(1) /* top border */ +
      rpx(10) /* pad top */ +
      rpx(14) /* header line */ +
      rpx(8) /* gap */ +
      rpx(kStepCircleSize) /* circle */ +
      rpx(4) /* gap to label */ +
      rpx(14) /* label line */ +
      rpx(2) +
      rpx(12) /* view status */ +
      rpx(10); /* pad bottom */
    return {
      height: headerH + sep1 + gridH + sep2 + statusH + progressH,
    };
  }, [rpx]);
};
